import { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, FileText, Calendar, Clock, User, Eye, Shell, Syringe, Printer } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { MediaUploader, MediaViewer } from '@/components/MediaUploader';
import { exportElementToPDF } from '@/utils/pdfExport';
import { formatDate } from '@/utils/dateFormat';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeader = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

const PENS = ['Pen 1', 'Pen 2', 'Pen 3', 'Pen 4', 'Pen 5', 'Pen 6', 'Brooding Pen', 'Grower Pen'];
const RATINGS = ['Good', 'Fair', 'Poor'];
const LITTER_RATINGS = ['Good', 'Fair', 'Signs of Infection'];
const LIGHTING_RATINGS = ['Adequate', 'Poor', 'Requires more lighting'];
const MORTALITY_RATINGS = ['Low', 'Medium', 'High', 'Alarming'];
const CLINICAL_OBSERVATIONS = ['None', 'Respiratory Distress', 'Lameness/Leg Issue', 'Nervous Signs (Torticollis/Paralysis)', 'Parasites'];

const VetDoctorReport = () => {
  const [reports, setReports] = useState([]);
  const [open, setOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [editingReport, setEditingReport] = useState(null);
  const [viewingReport, setViewingReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pdfBusy, setPdfBusy] = useState(false);
  const vetViewRef = useRef(null);

  const downloadVetPDF = async () => {
    if (!vetViewRef.current || !viewingReport) return;
    setPdfBusy(true);
    try {
      await exportElementToPDF(vetViewRef.current, `vet-report-${viewingReport.vet_doctor_name}-${viewingReport.visitation_date}.pdf`);
      toast.success('PDF downloaded');
    } catch (e) {
      toast.error('Failed to generate PDF');
    } finally {
      setPdfBusy(false);
    }
  };

  const getInitialFormData = () => ({
    visitation_date: new Date().toISOString().split('T')[0],
    visitation_time: new Date().toTimeString().slice(0, 5),
    vet_doctor_name: '',
    // Assessments per pen
    flock_assessment: { pen1: 'Good', pen2: 'Good', pen3: 'Good', pen4: 'Good', pen5: 'Good' },
    feed_consumption: { pen1: 'Good', pen2: 'Good', pen3: 'Good', pen4: 'Good', pen5: 'Good' },
    feed_quality: 'Good',
    ventilation: { pen1: 'Good', pen2: 'Good', pen3: 'Good', pen4: 'Good', pen5: 'Good' },
    water_intake: { pen1: 'Good', pen2: 'Good', pen3: 'Good', pen4: 'Good', pen5: 'Good' },
    feather_condition: { pen1: 'Good', pen2: 'Good', pen3: 'Good', pen4: 'Good', pen5: 'Good' },
    droppings_consistency: { pen1: 'Good', pen2: 'Good', pen3: 'Good', pen4: 'Good', pen5: 'Good' },
    litter_quality: { pen1: 'Good', pen2: 'Good', pen3: 'Good', pen4: 'Good', pen5: 'Good' },
    lighting_condition: { pen1: 'Adequate', pen2: 'Adequate', pen3: 'Adequate', pen4: 'Adequate', pen5: 'Adequate' },
    clinical_observations: { pen1: 'None', pen2: 'None', pen3: 'None', pen4: 'None', pen5: 'None' },
    mortality_rate: { pen1: 'Low', pen2: 'Low', pen3: 'Low', pen4: 'Low', pen5: 'Low' },
    // Census
    census: { pen1: '', pen2: '', pen3: '', pen4: '', pen5: '' },
    // Medications
    medication_administered: 'No',
    medications_list: '',
    medication_indications: '',
    // Training
    training_provided: 'No',
    training_description: '',
    // Recommendations
    recommendations: '',
    vaccination_recommendation: '',
    treatment_recommendation: '',
    // Snail House
    snail_observations: '',
    snail_census: '',
    snail_health: 'Good',
    // Media
    attachments: [],
    // Attestation
    attestation_name: '',
    next_visit_date: '',
    status: 'submitted'
  });

  const [formData, setFormData] = useState(getInitialFormData());

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    try {
      const response = await axios.get(`${API}/vet-reports`, getAuthHeader());
      setReports(response.data);
    } catch (error) {
      toast.error('Failed to fetch vet reports');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (statusVal) => {
    if (!formData.vet_doctor_name.trim()) {
      toast.error('Vet Doctor Name is required');
      return;
    }
    if (statusVal === 'submitted' && !formData.attestation_name.trim()) {
      toast.error('Attestation name is required to submit');
      return;
    }

    try {
      const payload = { ...formData, status: statusVal };
      if (editingReport) {
        await axios.put(`${API}/vet-reports/${editingReport.id}`, payload, getAuthHeader());
        toast.success(statusVal === 'draft' ? 'Draft saved — resume it anytime' : 'Report submitted successfully');
      } else {
        await axios.post(`${API}/vet-reports`, payload, getAuthHeader());
        toast.success(statusVal === 'draft' ? 'Draft saved — resume it anytime' : 'Report submitted successfully');
      }
      fetchReports();
      handleClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    handleSave('submitted');
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this report?')) return;
    try {
      await axios.delete(`${API}/vet-reports/${id}`, getAuthHeader());
      toast.success('Report deleted successfully');
      fetchReports();
    } catch (error) {
      toast.error('Failed to delete report');
    }
  };

  const handleEdit = (report) => {
    setEditingReport(report);
    setFormData({
      visitation_date: report.visitation_date || '',
      visitation_time: report.visitation_time || '',
      vet_doctor_name: report.vet_doctor_name || '',
      flock_assessment: report.flock_assessment || getInitialFormData().flock_assessment,
      feed_consumption: report.feed_consumption || getInitialFormData().feed_consumption,
      feed_quality: report.feed_quality || 'Good',
      ventilation: report.ventilation || getInitialFormData().ventilation,
      water_intake: report.water_intake || getInitialFormData().water_intake,
      feather_condition: report.feather_condition || getInitialFormData().feather_condition,
      droppings_consistency: report.droppings_consistency || getInitialFormData().droppings_consistency,
      litter_quality: report.litter_quality || getInitialFormData().litter_quality,
      lighting_condition: report.lighting_condition || getInitialFormData().lighting_condition,
      clinical_observations: report.clinical_observations || getInitialFormData().clinical_observations,
      mortality_rate: report.mortality_rate || getInitialFormData().mortality_rate,
      census: report.census || getInitialFormData().census,
      medication_administered: report.medication_administered || 'No',
      medications_list: report.medications_list || '',
      medication_indications: report.medication_indications || '',
      training_provided: report.training_provided || 'No',
      training_description: report.training_description || '',
      recommendations: report.recommendations || '',
      vaccination_recommendation: report.vaccination_recommendation || '',
      treatment_recommendation: report.treatment_recommendation || '',
      snail_observations: report.snail_observations || '',
      snail_census: report.snail_census || '',
      snail_health: report.snail_health || 'Good',
      attachments: report.attachments || [],
      attestation_name: report.attestation_name || '',
      next_visit_date: report.next_visit_date || '',
      status: report.status || 'submitted'
    });
    setOpen(true);
  };

  const handleView = (report) => {
    setViewingReport(report);
    setViewOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditingReport(null);
    setFormData(getInitialFormData());
  };

  const updatePenField = (field, pen, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: { ...prev[field], [pen]: value }
    }));
  };

  const renderPenAssessmentGrid = (field, options, label) => (
    <div className="border rounded-lg p-4 bg-slate-50">
      <h4 className="font-semibold text-slate-700 mb-3">{label}</h4>
      <div className="grid grid-cols-6 gap-2 text-sm">
        <div className="font-medium text-slate-500">Pen</div>
        {PENS.map((pen, idx) => (
          <div key={pen} className="font-medium text-center text-slate-600">{idx + 1}</div>
        ))}
        <div className="font-medium text-slate-500">Rating</div>
        {['pen1', 'pen2', 'pen3', 'pen4', 'pen5'].map((penKey) => (
          <Select
            key={penKey}
            value={formData[field][penKey]}
            onValueChange={(value) => updatePenField(field, penKey, value)}
          >
            <SelectTrigger className="h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {options.map((option) => (
                <SelectItem key={option} value={option}>{option}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        ))}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-slate-500">Loading vet reports...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="vet-doctor-report-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Vet Doctor Visitation Report
          </h1>
          <p className="text-lg text-slate-600 mt-2">Majeroku, Ikire Farm Location</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-vet-report-button" className="bg-emerald-600 hover:bg-emerald-700">
              <Plus className="w-4 h-4 mr-2" />
              New Report
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="vet-report-dialog">
            <DialogHeader>
              <DialogTitle>{editingReport ? 'Edit Report' : 'Vet Doctor Visitation Report'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Basic Information */}
              <div className="border rounded-lg p-4 bg-blue-50">
                <h3 className="font-semibold text-blue-800 mb-4">Basic Information</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="visitation_date">Date of Visitation *</Label>
                    <Input
                      id="visitation_date"
                      type="date"
                      data-testid="visitation-date-input"
                      value={formData.visitation_date}
                      onChange={(e) => setFormData({ ...formData, visitation_date: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="visitation_time">Time of Visitation *</Label>
                    <Input
                      id="visitation_time"
                      type="time"
                      data-testid="visitation-time-input"
                      value={formData.visitation_time}
                      onChange={(e) => setFormData({ ...formData, visitation_time: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="vet_doctor_name">Visiting Vet Doctor's Name *</Label>
                    <Input
                      id="vet_doctor_name"
                      data-testid="vet-doctor-name-input"
                      value={formData.vet_doctor_name}
                      onChange={(e) => setFormData({ ...formData, vet_doctor_name: e.target.value })}
                      placeholder="Dr. John Smith"
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Assessments */}
              <div className="space-y-4">
                <h3 className="font-semibold text-slate-800">Pen Assessments</h3>
                {renderPenAssessmentGrid('flock_assessment', RATINGS, 'General Flock Assessment (Activities/Alertness)')}
                {renderPenAssessmentGrid('feed_consumption', RATINGS, 'Feed Consumption')}
                
                <div className="border rounded-lg p-4 bg-slate-50">
                  <Label htmlFor="feed_quality">Feed Quality (Overall)</Label>
                  <Select
                    value={formData.feed_quality}
                    onValueChange={(value) => setFormData({ ...formData, feed_quality: value })}
                  >
                    <SelectTrigger className="w-48 mt-2">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {RATINGS.map((option) => (
                        <SelectItem key={option} value={option}>{option}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {renderPenAssessmentGrid('ventilation', RATINGS, 'Ventilation/Air Quality')}
                {renderPenAssessmentGrid('water_intake', RATINGS, 'Water Intake')}
                {renderPenAssessmentGrid('feather_condition', RATINGS, 'Feather Condition')}
                {renderPenAssessmentGrid('droppings_consistency', RATINGS, 'Droppings Consistency')}
                {renderPenAssessmentGrid('litter_quality', LITTER_RATINGS, 'Litter Quality')}
                {renderPenAssessmentGrid('lighting_condition', LIGHTING_RATINGS, 'Lighting Condition')}
                {renderPenAssessmentGrid('clinical_observations', CLINICAL_OBSERVATIONS, 'Clinical Observations')}
                {renderPenAssessmentGrid('mortality_rate', MORTALITY_RATINGS, 'Mortality Rate')}
              </div>

              {/* Census */}
              <div className="border rounded-lg p-4 bg-amber-50">
                <h3 className="font-semibold text-amber-800 mb-4">Census (Bird Count per Pen)</h3>
                <div className="grid grid-cols-5 gap-4">
                  {['pen1', 'pen2', 'pen3', 'pen4', 'pen5'].map((penKey, idx) => (
                    <div key={penKey}>
                      <Label>Pen {idx + 1}</Label>
                      <Input
                        type="number"
                        value={formData.census[penKey]}
                        onChange={(e) => updatePenField('census', penKey, e.target.value)}
                        placeholder="0"
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Medications */}
              <div className="border rounded-lg p-4 bg-red-50">
                <h3 className="font-semibold text-red-800 mb-4">Medications</h3>
                <div className="space-y-4">
                  <div>
                    <Label>Was any Medication Administered during this visit?</Label>
                    <Select
                      value={formData.medication_administered}
                      onValueChange={(value) => setFormData({ ...formData, medication_administered: value })}
                    >
                      <SelectTrigger className="w-48 mt-2">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="No">No</SelectItem>
                        <SelectItem value="Yes">Yes</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {formData.medication_administered === 'Yes' && (
                    <>
                      <div>
                        <Label>List medications administered and in which Pen</Label>
                        <Textarea
                          value={formData.medications_list}
                          onChange={(e) => setFormData({ ...formData, medications_list: e.target.value })}
                          placeholder="e.g., Antibiotic X administered in Pen 2 and 3"
                          className="mt-2"
                        />
                      </div>
                      <div>
                        <Label>Indications for the medications (what illness is being treated)</Label>
                        <Textarea
                          value={formData.medication_indications}
                          onChange={(e) => setFormData({ ...formData, medication_indications: e.target.value })}
                          placeholder="e.g., Respiratory infection treatment"
                          className="mt-2"
                        />
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Training */}
              <div className="border rounded-lg p-4 bg-purple-50">
                <h3 className="font-semibold text-purple-800 mb-4">Staff Training</h3>
                <div className="space-y-4">
                  <div>
                    <Label>Was Training provided to staff on site during this visit?</Label>
                    <Select
                      value={formData.training_provided}
                      onValueChange={(value) => setFormData({ ...formData, training_provided: value })}
                    >
                      <SelectTrigger className="w-48 mt-2">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="No">No</SelectItem>
                        <SelectItem value="Yes">Yes</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {formData.training_provided === 'Yes' && (
                    <div>
                      <Label>Brief explanation of training provided</Label>
                      <Textarea
                        value={formData.training_description}
                        onChange={(e) => setFormData({ ...formData, training_description: e.target.value })}
                        placeholder="e.g., Proper medication administration techniques"
                        className="mt-2"
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Recommendations */}
              <div className="border rounded-lg p-4 bg-emerald-50">
                <h3 className="font-semibold text-emerald-800 mb-4">Recommendations</h3>
                <Textarea
                  value={formData.recommendations}
                  onChange={(e) => setFormData({ ...formData, recommendations: e.target.value })}
                  placeholder="e.g., Vitamin C and multivitamin would be needed to help the birds with heat stress."
                  rows={4}
                />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <div>
                    <Label className="flex items-center gap-1"><Syringe className="w-3.5 h-3.5" /> Required / Recommended Vaccination</Label>
                    <Textarea
                      data-testid="vaccination-recommendation-input"
                      value={formData.vaccination_recommendation}
                      onChange={(e) => setFormData({ ...formData, vaccination_recommendation: e.target.value })}
                      placeholder="e.g., Lasota (Newcastle) booster due next week"
                      rows={3}
                    />
                  </div>
                  <div>
                    <Label>Recommended Treatment (if outbreak present)</Label>
                    <Textarea
                      data-testid="treatment-recommendation-input"
                      value={formData.treatment_recommendation}
                      onChange={(e) => setFormData({ ...formData, treatment_recommendation: e.target.value })}
                      placeholder="e.g., Antibiotic course for suspected CRD"
                      rows={3}
                    />
                  </div>
                </div>
              </div>

              {/* Snail House */}
              <div className="border rounded-lg p-4 bg-amber-50">
                <h3 className="font-semibold text-amber-800 mb-4 flex items-center gap-2"><Shell className="w-4 h-4" /> Snail Green House</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label>Snail Health</Label>
                    <Select value={formData.snail_health} onValueChange={(v) => setFormData({ ...formData, snail_health: v })}>
                      <SelectTrigger data-testid="snail-health-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{RATINGS.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Snail Census (headcount)</Label>
                    <Input type="number" value={formData.snail_census} onChange={(e) => setFormData({ ...formData, snail_census: e.target.value })} placeholder="e.g., 1200" />
                  </div>
                </div>
                <div className="mt-3">
                  <Label>Snail Observations</Label>
                  <Textarea
                    value={formData.snail_observations}
                    onChange={(e) => setFormData({ ...formData, snail_observations: e.target.value })}
                    placeholder="Visual observations of the snailery, moisture, feeding, mortality, etc."
                    rows={3}
                  />
                </div>
              </div>

              {/* Media */}
              <div className="border rounded-lg p-4 bg-slate-50">
                <h3 className="font-semibold text-slate-800 mb-2">Photos / Videos (optional)</h3>
                <p className="text-sm text-slate-500 mb-3">Attach supporting images or short videos (max 50MB each).</p>
                <MediaUploader label="" attachments={formData.attachments} setAttachments={(v) => setFormData({ ...formData, attachments: v })} />
              </div>

              {/* Attestation */}
              <div className="border rounded-lg p-4 bg-slate-100">
                <h3 className="font-semibold text-slate-800 mb-4">Attestation & Next Visit</h3>
                <p className="text-sm text-slate-600 mb-4">
                  By writing your name below, you attest to completing this form as required by Wilyrash Farm under the Vet consultancy agreement.
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="attestation_name">Your Full Name *</Label>
                    <Input
                      id="attestation_name"
                      data-testid="attestation-name-input"
                      value={formData.attestation_name}
                      onChange={(e) => setFormData({ ...formData, attestation_name: e.target.value })}
                      placeholder="Dr. Ariyo Adebayo"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="next_visit_date">Date of Next Visit (Optional)</Label>
                    <Input
                      id="next_visit_date"
                      type="date"
                      value={formData.next_visit_date}
                      onChange={(e) => setFormData({ ...formData, next_visit_date: e.target.value })}
                    />
                  </div>
                </div>
              </div>

              <div className="sticky bottom-0 -mx-6 -mb-6 px-6 py-4 bg-white border-t border-slate-200 flex flex-wrap gap-2 z-10">
                <Button type="submit" data-testid="submit-vet-report-button" className="flex-1 min-w-[120px] bg-emerald-600 hover:bg-emerald-700">
                  {editingReport ? 'Update Report' : 'Submit Report'}
                </Button>
                <Button type="button" variant="outline" onClick={() => handleSave('draft')} data-testid="save-vet-draft-button">
                  Save as Draft
                </Button>
                <Button type="button" variant="outline" onClick={handleClose}>
                  Cancel
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Reports Table */}
      <div className="card">
        <div className="overflow-x-auto">
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Time</th>
                <th>Vet Doctor</th>
                <th>Feed Quality</th>
                <th>Medication</th>
                <th>Next Visit</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody data-testid="vet-reports-table-body">
              {reports.map((report) => (
                <tr key={report.id}>
                  <td>{formatDate(report.visitation_date)}</td>
                  <td>{report.visitation_time || '-'}</td>
                  <td className="font-medium">
                    {report.vet_doctor_name}
                    {report.status === 'draft' && (
                      <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium" data-testid={`vet-draft-badge-${report.id}`}>Draft</span>
                    )}
                  </td>
                  <td>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      report.feed_quality === 'Good' ? 'bg-emerald-100 text-emerald-700' :
                      report.feed_quality === 'Fair' ? 'bg-amber-100 text-amber-700' :
                      'bg-red-100 text-red-700'
                    }`}>
                      {report.feed_quality}
                    </span>
                  </td>
                  <td>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      report.medication_administered === 'Yes' ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-700'
                    }`}>
                      {report.medication_administered}
                    </span>
                  </td>
                  <td>{report.next_visit_date ? formatDate(report.next_visit_date) : '-'}</td>
                  <td>
                    <div className="flex gap-2">
                      <button
                        data-testid={`view-report-${report.id}`}
                        onClick={() => handleView(report)}
                        className="p-2 text-emerald-600 hover:bg-emerald-50 rounded transition-colors"
                        title="View Report"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button
                        data-testid={`edit-report-${report.id}`}
                        onClick={() => handleEdit(report)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                        title="Edit Report"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        data-testid={`delete-report-${report.id}`}
                        onClick={() => handleDelete(report.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded transition-colors"
                        title="Delete Report"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {reports.length === 0 && (
            <div className="text-center py-12 text-slate-500">
              No vet reports yet. Create your first report!
            </div>
          )}
        </div>
      </div>

      {/* View Report Dialog */}
      <Dialog open={viewOpen} onOpenChange={setViewOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-start justify-between gap-3">
              <DialogTitle>Vet Visitation Report Details</DialogTitle>
              <Button size="sm" variant="outline" onClick={downloadVetPDF} disabled={pdfBusy} className="gap-1 flex-shrink-0" data-testid="download-vet-pdf-btn">
                {pdfBusy ? <div className="animate-spin w-4 h-4 border-2 border-slate-400 border-t-transparent rounded-full" /> : <Printer className="w-4 h-4" />}
                PDF
              </Button>
            </div>
          </DialogHeader>
          {viewingReport && (
            <div ref={vetViewRef} className="space-y-6 bg-white p-2">
              {/* Basic Info */}
              <div className="grid grid-cols-3 gap-4 p-4 bg-blue-50 rounded-lg">
                <div>
                  <p className="text-sm text-slate-500">Date</p>
                  <p className="font-semibold">{formatDate(viewingReport.visitation_date)}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Time</p>
                  <p className="font-semibold">{viewingReport.visitation_time || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Vet Doctor</p>
                  <p className="font-semibold">{viewingReport.vet_doctor_name}</p>
                </div>
              </div>

              {/* Pen Assessments Grid */}
              <div className="border rounded-lg overflow-hidden">
                <div className="bg-slate-100 p-3">
                  <h4 className="font-semibold text-slate-800">Pen Assessments</h4>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="p-2 text-left font-medium text-slate-600">Assessment</th>
                        {['Pen 1', 'Pen 2', 'Pen 3', 'Pen 4', 'Pen 5'].map((pen) => (
                          <th key={pen} className="p-2 text-center font-medium text-slate-600">{pen}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      <tr>
                        <td className="p-2 font-medium">Flock Assessment</td>
                        {['pen1', 'pen2', 'pen3', 'pen4', 'pen5'].map((pen) => (
                          <td key={pen} className="p-2 text-center">
                            <span className={`px-2 py-1 rounded text-xs ${
                              viewingReport.flock_assessment?.[pen] === 'Good' ? 'bg-emerald-100 text-emerald-700' :
                              viewingReport.flock_assessment?.[pen] === 'Fair' ? 'bg-amber-100 text-amber-700' :
                              'bg-red-100 text-red-700'
                            }`}>{viewingReport.flock_assessment?.[pen] || '-'}</span>
                          </td>
                        ))}
                      </tr>
                      <tr className="bg-slate-25">
                        <td className="p-2 font-medium">Feed Consumption</td>
                        {['pen1', 'pen2', 'pen3', 'pen4', 'pen5'].map((pen) => (
                          <td key={pen} className="p-2 text-center">
                            <span className={`px-2 py-1 rounded text-xs ${
                              viewingReport.feed_consumption?.[pen] === 'Good' ? 'bg-emerald-100 text-emerald-700' :
                              viewingReport.feed_consumption?.[pen] === 'Fair' ? 'bg-amber-100 text-amber-700' :
                              'bg-red-100 text-red-700'
                            }`}>{viewingReport.feed_consumption?.[pen] || '-'}</span>
                          </td>
                        ))}
                      </tr>
                      <tr>
                        <td className="p-2 font-medium">Ventilation/Air Quality</td>
                        {['pen1', 'pen2', 'pen3', 'pen4', 'pen5'].map((pen) => (
                          <td key={pen} className="p-2 text-center">
                            <span className={`px-2 py-1 rounded text-xs ${
                              viewingReport.ventilation?.[pen] === 'Good' ? 'bg-emerald-100 text-emerald-700' :
                              viewingReport.ventilation?.[pen] === 'Fair' ? 'bg-amber-100 text-amber-700' :
                              'bg-red-100 text-red-700'
                            }`}>{viewingReport.ventilation?.[pen] || '-'}</span>
                          </td>
                        ))}
                      </tr>
                      <tr className="bg-slate-25">
                        <td className="p-2 font-medium">Water Intake</td>
                        {['pen1', 'pen2', 'pen3', 'pen4', 'pen5'].map((pen) => (
                          <td key={pen} className="p-2 text-center">
                            <span className={`px-2 py-1 rounded text-xs ${
                              viewingReport.water_intake?.[pen] === 'Good' ? 'bg-emerald-100 text-emerald-700' :
                              viewingReport.water_intake?.[pen] === 'Fair' ? 'bg-amber-100 text-amber-700' :
                              'bg-red-100 text-red-700'
                            }`}>{viewingReport.water_intake?.[pen] || '-'}</span>
                          </td>
                        ))}
                      </tr>
                      <tr>
                        <td className="p-2 font-medium">Feather Condition</td>
                        {['pen1', 'pen2', 'pen3', 'pen4', 'pen5'].map((pen) => (
                          <td key={pen} className="p-2 text-center">
                            <span className={`px-2 py-1 rounded text-xs ${
                              viewingReport.feather_condition?.[pen] === 'Good' ? 'bg-emerald-100 text-emerald-700' :
                              viewingReport.feather_condition?.[pen] === 'Fair' ? 'bg-amber-100 text-amber-700' :
                              'bg-red-100 text-red-700'
                            }`}>{viewingReport.feather_condition?.[pen] || '-'}</span>
                          </td>
                        ))}
                      </tr>
                      <tr className="bg-slate-25">
                        <td className="p-2 font-medium">Droppings Consistency</td>
                        {['pen1', 'pen2', 'pen3', 'pen4', 'pen5'].map((pen) => (
                          <td key={pen} className="p-2 text-center">
                            <span className={`px-2 py-1 rounded text-xs ${
                              viewingReport.droppings_consistency?.[pen] === 'Good' ? 'bg-emerald-100 text-emerald-700' :
                              viewingReport.droppings_consistency?.[pen] === 'Fair' ? 'bg-amber-100 text-amber-700' :
                              'bg-red-100 text-red-700'
                            }`}>{viewingReport.droppings_consistency?.[pen] || '-'}</span>
                          </td>
                        ))}
                      </tr>
                      <tr>
                        <td className="p-2 font-medium">Litter Quality</td>
                        {['pen1', 'pen2', 'pen3', 'pen4', 'pen5'].map((pen) => (
                          <td key={pen} className="p-2 text-center">
                            <span className={`px-2 py-1 rounded text-xs ${
                              viewingReport.litter_quality?.[pen] === 'Good' ? 'bg-emerald-100 text-emerald-700' :
                              viewingReport.litter_quality?.[pen] === 'Fair' ? 'bg-amber-100 text-amber-700' :
                              'bg-red-100 text-red-700'
                            }`}>{viewingReport.litter_quality?.[pen] || '-'}</span>
                          </td>
                        ))}
                      </tr>
                      <tr className="bg-slate-25">
                        <td className="p-2 font-medium">Lighting Condition</td>
                        {['pen1', 'pen2', 'pen3', 'pen4', 'pen5'].map((pen) => (
                          <td key={pen} className="p-2 text-center">
                            <span className={`px-2 py-1 rounded text-xs ${
                              viewingReport.lighting_condition?.[pen] === 'Adequate' ? 'bg-emerald-100 text-emerald-700' :
                              viewingReport.lighting_condition?.[pen] === 'Dim' ? 'bg-amber-100 text-amber-700' :
                              'bg-red-100 text-red-700'
                            }`}>{viewingReport.lighting_condition?.[pen] || '-'}</span>
                          </td>
                        ))}
                      </tr>
                      <tr>
                        <td className="p-2 font-medium">Clinical Observations</td>
                        {['pen1', 'pen2', 'pen3', 'pen4', 'pen5'].map((pen) => (
                          <td key={pen} className="p-2 text-center">
                            <span className={`px-2 py-1 rounded text-xs ${
                              viewingReport.clinical_observations?.[pen] === 'None' ? 'bg-emerald-100 text-emerald-700' :
                              'bg-amber-100 text-amber-700'
                            }`}>{viewingReport.clinical_observations?.[pen] || '-'}</span>
                          </td>
                        ))}
                      </tr>
                      <tr className="bg-slate-25">
                        <td className="p-2 font-medium">Mortality Rate</td>
                        {['pen1', 'pen2', 'pen3', 'pen4', 'pen5'].map((pen) => (
                          <td key={pen} className="p-2 text-center">
                            <span className={`px-2 py-1 rounded text-xs ${
                              viewingReport.mortality_rate?.[pen] === 'Low' ? 'bg-emerald-100 text-emerald-700' :
                              viewingReport.mortality_rate?.[pen] === 'Medium' ? 'bg-amber-100 text-amber-700' :
                              'bg-red-100 text-red-700'
                            }`}>{viewingReport.mortality_rate?.[pen] || '-'}</span>
                          </td>
                        ))}
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Feed Quality */}
              <div className="p-4 bg-blue-50 rounded-lg">
                <h4 className="font-semibold text-blue-800 mb-2">Overall Feed Quality</h4>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                  viewingReport.feed_quality === 'Good' ? 'bg-emerald-100 text-emerald-700' :
                  viewingReport.feed_quality === 'Fair' ? 'bg-amber-100 text-amber-700' :
                  'bg-red-100 text-red-700'
                }`}>{viewingReport.feed_quality}</span>
              </div>

              {/* Census */}
              {viewingReport.census && (
                <div className="p-4 bg-amber-50 rounded-lg">
                  <h4 className="font-semibold text-amber-800 mb-2">Bird Census</h4>
                  <div className="grid grid-cols-5 gap-4">
                    {['pen1', 'pen2', 'pen3', 'pen4', 'pen5'].map((pen, idx) => (
                      <div key={pen} className="text-center">
                        <p className="text-sm text-slate-500">Pen {idx + 1}</p>
                        <p className="font-bold text-lg">{viewingReport.census[pen] || '-'}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Medications */}
              <div className="p-4 bg-red-50 rounded-lg">
                <h4 className="font-semibold text-red-800 mb-2">Medications</h4>
                <p className="text-slate-700 mb-2">
                  <span className="font-medium">Administered:</span>{' '}
                  <span className={`px-2 py-1 rounded text-xs ${
                    viewingReport.medication_administered === 'Yes' ? 'bg-red-200 text-red-800' : 'bg-slate-200 text-slate-700'
                  }`}>{viewingReport.medication_administered}</span>
                </p>
                {viewingReport.medication_administered === 'Yes' && (
                  <>
                    {viewingReport.medications_list && (
                      <p className="text-slate-700 mb-1"><span className="font-medium">Medications:</span> {viewingReport.medications_list}</p>
                    )}
                    {viewingReport.medication_indications && (
                      <p className="text-slate-600 text-sm"><span className="font-medium">Indications:</span> {viewingReport.medication_indications}</p>
                    )}
                  </>
                )}
              </div>

              {/* Training */}
              <div className="p-4 bg-purple-50 rounded-lg">
                <h4 className="font-semibold text-purple-800 mb-2">Staff Training</h4>
                <p className="text-slate-700 mb-2">
                  <span className="font-medium">Training Provided:</span>{' '}
                  <span className={`px-2 py-1 rounded text-xs ${
                    viewingReport.training_provided === 'Yes' ? 'bg-purple-200 text-purple-800' : 'bg-slate-200 text-slate-700'
                  }`}>{viewingReport.training_provided}</span>
                </p>
                {viewingReport.training_provided === 'Yes' && viewingReport.training_description && (
                  <p className="text-slate-700"><span className="font-medium">Description:</span> {viewingReport.training_description}</p>
                )}
              </div>

              {/* Recommendations */}
              {viewingReport.recommendations && (
                <div className="p-4 bg-emerald-50 rounded-lg">
                  <h4 className="font-semibold text-emerald-800 mb-2">Recommendations</h4>
                  <p className="text-slate-700 whitespace-pre-wrap">{viewingReport.recommendations}</p>
                </div>
              )}

              {(viewingReport.vaccination_recommendation || viewingReport.treatment_recommendation) && (
                <div className="p-4 bg-sky-50 rounded-lg space-y-2">
                  {viewingReport.vaccination_recommendation && (
                    <p className="text-slate-700"><span className="font-medium">Vaccination:</span> {viewingReport.vaccination_recommendation}</p>
                  )}
                  {viewingReport.treatment_recommendation && (
                    <p className="text-slate-700"><span className="font-medium">Recommended Treatment:</span> {viewingReport.treatment_recommendation}</p>
                  )}
                </div>
              )}

              {(viewingReport.snail_observations || viewingReport.snail_census || viewingReport.snail_health) && (
                <div className="p-4 bg-amber-50 rounded-lg">
                  <h4 className="font-semibold text-amber-800 mb-2">Snail Green House</h4>
                  {viewingReport.snail_health && <p className="text-slate-700"><span className="font-medium">Health:</span> {viewingReport.snail_health}</p>}
                  {viewingReport.snail_census && <p className="text-slate-700"><span className="font-medium">Census:</span> {viewingReport.snail_census}</p>}
                  {viewingReport.snail_observations && <p className="text-slate-700 whitespace-pre-wrap mt-1">{viewingReport.snail_observations}</p>}
                </div>
              )}

              {viewingReport.attachments?.length > 0 && (
                <div className="p-4 bg-slate-50 rounded-lg">
                  <h4 className="font-semibold text-slate-800 mb-2">Photos / Videos</h4>
                  <MediaViewer attachments={viewingReport.attachments} />
                </div>
              )}

              {/* Attestation */}
              <div className="p-4 bg-slate-100 rounded-lg">
                <h4 className="font-semibold text-slate-800 mb-2">Attestation</h4>
                <p className="text-slate-700">
                  <span className="font-medium">Attested by:</span> {viewingReport.attestation_name}
                </p>
                {viewingReport.next_visit_date && (
                  <p className="text-slate-600 mt-1">
                    <span className="font-medium">Next Visit:</span> {formatDate(viewingReport.next_visit_date)}
                  </p>
                )}
                {viewingReport.created_at && (
                  <p className="text-slate-500 text-sm mt-2">
                    Report created: {new Date(viewingReport.created_at).toLocaleString('en-GB', { timeZone: 'Africa/Lagos', day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true })}
                  </p>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VetDoctorReport;
